import React, { useEffect, useState } from "react";
import { resetPassword, confirmResetPassword } from "aws-amplify/auth";

import { Button, Stack, Alert, Collapse, Stepper, Step, StepLabel, StepContent, Box } from "@mui/material";
import { useTranslation } from "react-i18next";

import { LoadingButton } from "@mui/lab";
import { useForm } from "react-hook-form";
import SendIcon from "@mui/icons-material/Send";
import _ from "lodash";
import ConfirmationCodeInput from "../Inputs/ConfirmationCodeInput";
import UsernameInput from "../Inputs/UsernameInput";
import ConfirmedPasswordInput from "../Inputs/ConfirmedPasswordInput";

const PasswordResetForm = ({ setPasswordReset, setPasswordChanged }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [username, setUserName] = useState("");
  const { t } = useTranslation();

  const nextStep = () => {
    const step = Math.min(activeStep + 1, 1);
    setActiveStep(step);
  };
  const previousStep = () => {
    const step = Math.max(activeStep - 1, 0);
    setActiveStep(step);
  };

  return (
    <Stack direction="column" gap={2}>
      <Stepper orientation="vertical" activeStep={activeStep}>
        <Step>
          <SendCodeStep nextStep={nextStep} setUserName={setUserName} />
        </Step>
        <Step>
          <UpdatePasswordStep
            previousStep={previousStep}
            username={username}
            setPasswordReset={setPasswordReset}
            setPasswordChanged={setPasswordChanged}
          />
        </Step>
      </Stepper>
      <Button sx={{ marginTop: "2px", color: "#000" }} size="small" onClick={() => setPasswordReset(false)}>
        {t("L1_PASSWORD_RESET_BACK_TO_LOGIN")}
      </Button>
    </Stack>
  );
};

export default PasswordResetForm;

const SendCodeStep = (props) => {
  const { nextStep, setUserName } = props;
  const [error, setError] = useState(false);
  const formController = useForm({ mode: "onChange" });
  const {
    reset,
    handleSubmit,
    formState: { isValid, isDirty, isSubmitting },
  } = formController;
  const { t } = useTranslation();

  const sendResetCode = async (data) => {
    try {
      await resetPassword({ username: data.username });
      setUserName(data.username);
      nextStep();
    } catch (error) {
      setError(error);
      console.log(error);
      reset();
    }
  };
  useEffect(() => {
    isDirty && setError(false);
  }, [isDirty]);

  const loadingButtonStyle = {
    backgroundColor: "#66cc99",
    color: "#000000",
    "&:hover": {
      backgroundColor: "#37a46e",
    },
  };

  return (
    <>
      <StepLabel> {t("BUTTON_SEND_CODE")}</StepLabel>
      <StepContent>
        <form onSubmit={handleSubmit(sendResetCode)}>
          <Stack direction="column" gap={2}>
            <UsernameInput label={t("L1_INPUT_ENTER_YOUR_EMAIL")} formController={formController} />

            {error && <Alert severity="error">{_.get(error, "message", "")}</Alert>}

            <LoadingButton
              type="submit"
              size="small"
              variant="contained"
              startIcon={<SendIcon />}
              loading={isSubmitting}
              disabled={!isDirty || !isValid}
              sx={loadingButtonStyle}
            >
              {t("BUTTON_SEND_CODE")}
            </LoadingButton>
          </Stack>
        </form>
      </StepContent>
    </>
  );
};

const UpdatePasswordStep = (props) => {
  const { username } = props;
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const formController = useForm({ mode: "onChange" });
  const {
    handleSubmit,
    formState: { errors, isValid, isDirty },
  } = formController;
  const { t } = useTranslation();

  const validatePassword = (input) => {
    if (input.length < 8) {
      return "Password must be at least 8 characters long.";
    } else if (input.length > 99) {
      return "Password must be less than 99 characters long.";
    } else if (!/[A-Z]/.test(input)) {
      return "Password must include at least one uppercase letter.";
    } else if (!/[a-z]/.test(input)) {
      return "Password must include at least one lowercase letter.";
    } else if (!/[0-9]/.test(input)) {
      return "Password must include at least one number.";
    } else if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(input)) {
      return "Password must include at least one special character.";
    } else {
      return true;
    }
  };

  const updatePassword = async (data) => {
    setLoading(true);
    try {
      if (validatePassword(data.password) === true) {
        await confirmResetPassword({
          username: username,
          confirmationCode: data.code,
          newPassword: data.password,
        });
        props.setPasswordReset(false);
        props.setPasswordChanged(true);
      } else {
        setError(validatePassword(data.password));
        console.error(validatePassword(data.password));
      }
    } catch (error) {
      console.error(error);
      setError(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isDirty || isValid) {
      setError(null);
    }
  }, [isValid, isDirty]);

  const PasswordPolicyInfo =
    "Passwords need to be at least 8 characters long, include a uppercase letter, a lowercase letter, a number and a special character.";

  return (
    <>
      <StepLabel>{t("L1_SET_NEW_PASSWORD")}</StepLabel>
      <StepContent>
        <form onSubmit={handleSubmit(updatePassword)}>
          <Stack direction="column" gap={2}>
            <ConfirmationCodeInput label={t("CONFIRMATION_CODE_INPUT")} formController={formController} />
            <ConfirmedPasswordInput
              labelNewPassword={t("L1_INPUT_NEW_PASSWORD")}
              labelConfirmNewPw={t("CONFIRM_NEW_PW_INPUT")}
              formController={formController}
            />

            {error && (
              <Alert severity="error">
                <span>{PasswordPolicyInfo}</span>
              </Alert>
            )}

            <LoadingButton
              type="submit"
              size="small"
              variant="contained"
              disabled={!isValid || !isDirty}
              loading={loading}
              sx={{
                backgroundColor: "#000",
              }}
            >
              {t("L1_SET_NEW_PASSWORD")}
            </LoadingButton>
          </Stack>
        </form>
      </StepContent>
    </>
  );
};
